import React from 'react';
import { 
  Container, 
  Box, 
  Typography, 
  Paper,
  Divider,
} from '@mui/material';
import { motion } from 'framer-motion';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import { Link } from 'react-router-dom';
import logo from '../assets/BCL_nobg_crop.png';
import './Privacypolicy.css';

const PrivacyPolicy = () => {
  return (
    <Box className="privacy-section" sx={{ py: 8, background: 'linear-gradient(135deg, #f5f7fa 0%, #e8f4f8 100%)', minHeight: '100vh' }}>
      <Container maxWidth="lg">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Typography
            variant="h2"
            align="center"
            sx={{
              fontFamily: 'Quicksand, sans-serif',
              fontWeight: 700,
              color: '#51A8B1',
              mb: 1.5,
              fontSize: { xs: '2.5rem', md: '3.75rem' },
              letterSpacing: '-0.02em',
            }}
          >
            Privacy Policy
          </Typography>
          <Typography
            variant="body1"
            align="center"
            sx={{
              fontFamily: 'Quicksand, sans-serif',
              color: '#666',
              mb: 6,
              fontSize: '1.125rem',
              maxWidth: '600px',
              mx: 'auto',
            }}
          >
            How we collect, use, and protect your information
          </Typography>
        </motion.div>

        {/* Main Content */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
        >
          <Paper 
            elevation={3}
            sx={{ 
              p: { xs: 4, md: 8 },
              borderRadius: 4,
              background: 'linear-gradient(145deg, #ffffff 0%, #f9fafb 100%)',
              border: '1px solid rgba(81, 168, 177, 0.1)',
              boxShadow: '0 20px 60px rgba(81, 168, 177, 0.12)',
              mb: 6,
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontFamily: 'Quicksand, sans-serif',
                color: '#666',
                mb: 4,
                fontSize: '1rem',
                lineHeight: 1.8,
              }}
            >
              <strong>Effective Date:</strong> {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </Typography>

            {/* Introduction */}
            <Typography
              variant="h5"
              sx={{
                fontFamily: 'Barlow, sans-serif',
                fontWeight: 700,
                color: '#51A8B1',
                mb: 2,
                mt: 4,
              }}
            >
              Introduction
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontFamily: 'Quicksand, sans-serif',
                color: '#666',
                mb: 4,
                fontSize: '1rem',
                lineHeight: 1.8,
              }}
            >
              BioCircuit LABS ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services. Please read this policy carefully to understand our practices regarding your personal information.
            </Typography>

            {/* Information We Collect */}
            <Typography
              variant="h5"
              sx={{
                fontFamily: 'Barlow, sans-serif',
                fontWeight: 700,
                color: '#51A8B1',
                mb: 2,
                mt: 4,
              }}
            >
              Information We Collect
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontFamily: 'Quicksand, sans-serif',
                color: '#666',
                mb: 2,
                fontSize: '1rem',
                lineHeight: 1.8,
              }}
            >
              For accessing certain parts of our site, such as downloading documents, requesting services, or submitting inquiries, BioCircuit LABS may ask you to provide personal information about yourself by filling out an online form. The information we may collect includes:
            </Typography>
            <Box component="ul" sx={{ pl: 4, mb: 4 }}>
              <Typography
                component="li"
                sx={{
                  fontFamily: 'Quicksand, sans-serif',
                  color: '#666',
                  mb: 1.5,
                  fontSize: '1rem',
                  lineHeight: 1.8,
                }}
              >
                <strong>Personal Information:</strong> Name, email address, phone number, organization name, and any other information you voluntarily provide through our contact forms or service requests
              </Typography>
              <Typography
                component="li"
                sx={{
                  fontFamily: 'Quicksand, sans-serif',
                  color: '#666',
                  mb: 1.5,
                  fontSize: '1rem',
                  lineHeight: 1.8,
                }}
              >
                <strong>Technical Information:</strong> IP address, browser type, operating system, referring URLs, and other technical data collected automatically when you visit our site
              </Typography>
              <Typography
                component="li"
                sx={{
                  fontFamily: 'Quicksand, sans-serif',
                  color: '#666',
                  mb: 1.5,
                  fontSize: '1rem',
                  lineHeight: 1.8,
                }}
              >
                <strong>Usage Data:</strong> Information about how you interact with our website, including pages visited, time spent on pages, and navigation patterns
              </Typography>
            </Box>

            {/* How We Use Your Information */}
            <Typography
              variant="h5"
              sx={{
                fontFamily: 'Barlow, sans-serif',
                fontWeight: 700,
                color: '#51A8B1',
                mb: 2,
                mt: 4,
              }}
            >
              How We Use Your Information
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontFamily: 'Quicksand, sans-serif',
                color: '#666',
                mb: 2,
                fontSize: '1rem',
                lineHeight: 1.8,
              }}
            >
              The purpose of collecting this information is to:
            </Typography>
            <Box component="ul" sx={{ pl: 4, mb: 4 }}>
              <Typography
                component="li"
                sx={{
                  fontFamily: 'Quicksand, sans-serif',
                  color: '#666',
                  mb: 1.5,
                  fontSize: '1rem',
                  lineHeight: 1.8,
                }}
              >
                Process your requests and determine the nearest BioCircuit LABS representative according to your country and the type of request you submit
              </Typography>
              <Typography
                component="li"
                sx={{
                  fontFamily: 'Quicksand, sans-serif',
                  color: '#666',
                  mb: 1.5,
                  fontSize: '1rem',
                  lineHeight: 1.8,
                }}
              >
                Communicate with you regarding your inquiries, service requests, or other matters related to our business
              </Typography>
              <Typography
                component="li"
                sx={{
                  fontFamily: 'Quicksand, sans-serif',
                  color: '#666',
                  mb: 1.5,
                  fontSize: '1rem',
                  lineHeight: 1.8,
                }}
              >
                Improve our website and services based on your feedback and usage patterns
              </Typography>
              <Typography
                component="li"
                sx={{
                  fontFamily: 'Quicksand, sans-serif',
                  color: '#666',
                  mb: 1.5,
                  fontSize: '1rem',
                  lineHeight: 1.8,
                }}
              >
                Comply with legal obligations and protect our rights and interests
              </Typography>
            </Box>

            {/* Information Sharing */}
            <Typography
              variant="h5"
              sx={{
                fontFamily: 'Barlow, sans-serif',
                fontWeight: 700,
                color: '#51A8B1',
                mb: 2,
                mt: 4,
              }}
            >
              Information Sharing and Disclosure
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontFamily: 'Quicksand, sans-serif',
                color: '#666',
                mb: 2,
                fontSize: '1rem',
                lineHeight: 1.8,
              }}
            >
              The information you provide will not be communicated outside BioCircuit LABS to a third party that is not involved in processing your request, unless, in BioCircuit LABS's good faith judgment, required by law or judicial authorities. However, you acknowledge that any information provided by you through the site shall not be considered confidential by BioCircuit LABS.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontFamily: 'Quicksand, sans-serif',
                color: '#666',
                mb: 4,
                fontSize: '1rem',
                lineHeight: 1.8,
              }}
            >
              Such information may be copied, used, and stored by the relevant BioCircuit LABS representatives or units in accordance with local data privacy laws.
            </Typography>

            {/* Cookies */}
            <Typography
              variant="h5"
              sx={{
                fontFamily: 'Barlow, sans-serif',
                fontWeight: 700,
                color: '#51A8B1',
                mb: 2,
                mt: 4,
              }}
            >
              Cookies and Tracking Technologies
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontFamily: 'Quicksand, sans-serif',
                color: '#666',
                mb: 2,
                fontSize: '1rem',
                lineHeight: 1.8,
              }}
            >
              BioCircuit LABS may collect certain technical information about your computer and may use "cookies" to facilitate and track your use of our sites. A cookie is an element of data that a site can send to your browser, which may then store it on your system.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontFamily: 'Quicksand, sans-serif',
                color: '#666',
                mb: 4,
                fontSize: '1rem',
                lineHeight: 1.8,
              }}
            >
              For example, a cookie can be used to remember your login information if you choose the "Remember me" option while signing in. You are free to decline these cookies by setting your browser accordingly; however, refusing cookies may prevent you from accessing some parts of the site.
            </Typography>

            {/* Data Security */}
            <Typography
              variant="h5"
              sx={{
                fontFamily: 'Barlow, sans-serif',
                fontWeight: 700,
                color: '#51A8B1',
                mb: 2,
                mt: 4,
              }}
            >
              Data Security
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontFamily: 'Quicksand, sans-serif',
                color: '#666',
                mb: 4,
                fontSize: '1rem',
                lineHeight: 1.8,
              }}
            >
              We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, please note that no method of transmission over the Internet or electronic storage is completely secure, and we cannot guarantee absolute security of your data.
            </Typography>

            {/* Your Rights */}
            <Typography
              variant="h5"
              sx={{
                fontFamily: 'Barlow, sans-serif',
                fontWeight: 700,
                color: '#51A8B1',
                mb: 2,
                mt: 4,
              }}
            >
              Your Rights and Choices
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontFamily: 'Quicksand, sans-serif',
                color: '#666',
                mb: 2,
                fontSize: '1rem',
                lineHeight: 1.8,
              }}
            >
              Depending on your location, you may have certain rights regarding your personal information, including:
            </Typography>
            <Box component="ul" sx={{ pl: 4, mb: 4 }}>
              <Typography
                component="li"
                sx={{
                  fontFamily: 'Quicksand, sans-serif',
                  color: '#666',
                  mb: 1.5,
                  fontSize: '1rem',
                  lineHeight: 1.8,
                }}
              >
                The right to access and receive a copy of your personal information
              </Typography>
              <Typography
                component="li"
                sx={{
                  fontFamily: 'Quicksand, sans-serif',
                  color: '#666',
                  mb: 1.5,
                  fontSize: '1rem',
                  lineHeight: 1.8,
                }}
              >
                The right to correct inaccurate or incomplete information
              </Typography>
              <Typography
                component="li"
                sx={{
                  fontFamily: 'Quicksand, sans-serif',
                  color: '#666',
                  mb: 1.5,
                  fontSize: '1rem',
                  lineHeight: 1.8,
                }}
              >
                The right to request deletion of your personal information
              </Typography>
              <Typography
                component="li"
                sx={{
                  fontFamily: 'Quicksand, sans-serif',
                  color: '#666',
                  mb: 1.5,
                  fontSize: '1rem',
                  lineHeight: 1.8,
                }}
              >
                The right to object to or restrict certain processing activities
              </Typography>
            </Box>

            {/* Changes to Policy */}
            <Typography
              variant="h5"
              sx={{
                fontFamily: 'Barlow, sans-serif',
                fontWeight: 700,
                color: '#51A8B1',
                mb: 2,
                mt: 4,
              }}
            >
              Changes to This Privacy Policy
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontFamily: 'Quicksand, sans-serif',
                color: '#666',
                mb: 4,
                fontSize: '1rem',
                lineHeight: 1.8,
              }}
            >
              BioCircuit LABS reserves the right to modify this Privacy Policy at any time. We will notify you of any changes by posting the updated policy on this page with a new effective date. We encourage you to review this policy periodically to stay informed about how we protect your information.
            </Typography>

            {/* Contact Information */}
            <Typography
              variant="h5"
              sx={{
                fontFamily: 'Barlow, sans-serif',
                fontWeight: 700,
                color: '#51A8B1',
                mb: 2,
                mt: 4,
              }}
            >
              Contact Us
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontFamily: 'Quicksand, sans-serif',
                color: '#666',
                mb: 2,
                fontSize: '1rem',
                lineHeight: 1.8,
              }}
            >
              Please feel free to contact us if you have any questions or concerns about our privacy policy:
            </Typography>
            <Box sx={{ pl: 2, mb: 4 }}>
              <Typography
                variant="body1"
                sx={{
                  fontFamily: 'Quicksand, sans-serif',
                  color: '#666',
                  mb: 1,
                  fontSize: '1rem',
                  lineHeight: 1.8,
                }}
              >
                <strong>Email:</strong> info@biocircuitlabs.com
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontFamily: 'Quicksand, sans-serif',
                  color: '#666',
                  fontSize: '1rem',
                  lineHeight: 1.8,
                }}
              >
                <strong>Phone:</strong> +91 94141 74048
              </Typography>
            </Box>
          </Paper>
        </motion.div>
      </Container>

      {/* Footer Section */}
      <Box 
        component="footer" 
        sx={{ 
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          borderTop: '1px solid rgba(81, 168, 177, 0.1)',
          pt: 8,
          pb: 4,
          mt: 12
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 6 }}>
            {/* Logo and Company Info */}
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.3 }}
                >
                  <img 
                    src={logo} 
                    alt="BioCircuit Labs" 
                    style={{ height: '80px', marginRight: '15px' }}
                  />
                </motion.div>
                <Box>
                  <Typography
                    variant="h6"
                    component="span"
                    sx={{ 
                      fontFamily: 'Quicksand, sans-serif',
                      color: '#51A8B1',
                      fontWeight: 700,
                    }}
                  >
                    BioCircuit
                  </Typography>
                  <Typography
                    variant="h6"
                    component="span"
                    sx={{ 
                      fontFamily: 'Barlow, sans-serif',
                      color: '#A8CF45',
                      fontWeight: 700,
                      marginLeft: '8px'
                    }}
                  >
                    LABS
                  </Typography>
                </Box>
              </Box>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#666',
                  fontFamily: 'Quicksand, sans-serif',
                  mb: 2,
                  lineHeight: 1.7
                }}
              >
                Transforming healthcare operations through innovative technology solutions.
              </Typography>
            </Box>

            {/* Quick Links */}
            <Box sx={{ flex: 0.6 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontFamily: 'Barlow, sans-serif',
                  color: '#51A8B1',
                  fontWeight: 600,
                  mb: 3
                }}
              >
                Quick Links
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {[
                  { text: 'Home', path: '/' },
                  { text: 'Careers', path: '/careers' },
                  { text: 'Contact', path: '/contact' }
                ].map((item) => (
                  <motion.div
                    key={item.text}
                    whileHover={{ x: 5 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Link
                      to={item.path}
                      style={{ 
                        textDecoration: 'none',
                        fontFamily: 'Quicksand, sans-serif',
                        color: '#666',
                        fontSize: '0.95rem',
                        transition: 'color 0.3s ease'
                      }}
                      onMouseEnter={(e) => e.target.style.color = '#51A8B1'}
                      onMouseLeave={(e) => e.target.style.color = '#666'}
                    >
                      {item.text}
                    </Link>
                  </motion.div>
                ))}
              </Box>
            </Box>

            {/* Legal */}
            <Box sx={{ flex: 0.6 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontFamily: 'Barlow, sans-serif',
                  color: '#51A8B1',
                  fontWeight: 600,
                  mb: 3
                }}
              >
                Legal
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {[
                  { text: 'Privacy Policy', path: '/privacy-policy' },
                  { text: 'Terms & Conditions', path: '/terms-conditions' }
                ].map((item) => (
                  <motion.div
                    key={item.text}
                    whileHover={{ x: 5 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Link
                      to={item.path}
                      style={{ 
                        textDecoration: 'none',
                        fontFamily: 'Quicksand, sans-serif',
                        color: '#666',
                        fontSize: '0.95rem',
                        transition: 'color 0.3s ease'
                      }}
                      onMouseEnter={(e) => e.target.style.color = '#51A8B1'}
                      onMouseLeave={(e) => e.target.style.color = '#666'}
                    >
                      {item.text}
                    </Link>
                  </motion.div>
                ))}
              </Box>
            </Box>

            {/* Contact Info */}
            <Box sx={{ flex: 0.8 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontFamily: 'Barlow, sans-serif',
                  color: '#51A8B1',
                  fontWeight: 600,
                  mb: 3
                }}
              >
                Contact Us
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <EmailIcon sx={{ color: '#51A8B1', fontSize: '1.2rem' }} />
                  <Typography
                    sx={{ 
                      fontFamily: 'Quicksand, sans-serif',
                      color: '#666',
                      fontSize: '0.95rem'
                    }}
                  >
                    info@biocircuitlabs.com
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <PhoneIcon sx={{ color: '#51A8B1', fontSize: '1.2rem' }} />
                  <Typography
                    sx={{ 
                      fontFamily: 'Quicksand, sans-serif',
                      color: '#666',
                      fontSize: '0.95rem'
                    }}
                  >
                    +91 94141 74048
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Bottom Bar */}
          <Divider sx={{ my: 4, borderColor: 'rgba(81, 168, 177, 0.1)' }} />
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'center',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1
            }}
          >
            <Typography 
              variant="body2" 
              sx={{ 
                fontFamily: 'Quicksand, sans-serif',
                color: '#666',
                textAlign: 'center'
              }}
            >
              © {new Date().getFullYear()} BioCircuit Labs. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default PrivacyPolicy;